<template lang="pug">
//- VitePwaManifest
GlobalError(v-if="isEmbed")
.embed-error(v-if="isEmbed && isEmbedRedirectError")
  v-label Une erreur s'est produite lors de la redirection, cliquez pour recharger.
  v-btn.primary(@click="onReloadFromEmbed") Recharger
v-app
  inactive-lock(v-if="isUserLoged && !isEmbed && !isPopupWindow")
  .main(no-gutters)
    login(v-if="!isExternal && !isEmbed && !isLogout && !isPopupWindow")
    .overlay(v-if="isLoading")
      v-progress-circular.loader(indeterminate color="primary")
    .nav-bar(v-show="!isLoading && !isEmbed && !isPopupWindow && isUserLoged && !isMobile")
      Navbar(:user="user" :currentPath="currentPath" v-if="menuVisible")
    .content(v-show="!isLoading" :class="{ 'content-shifted': menuVisible }")
      slot
</template>

<script setup lang="ts">
import { useDisplay } from 'vuetify'
import { loadPriorityPatient } from '~/utils/patients/loadPriorityPatient'

const { signOut } = useAuth()

const route = useRoute()
const userStore = useUserStore()
const navigationStore = useNavigationStore()
const isLoading = ref(true)
const { mobile } = useDisplay()
const isMobile = ref<Boolean>(false)
const isEmbedRedirectError = ref<Boolean>(false)
const reloadUrl = ref<string | null>(null)
const { $host: host } = useNuxtApp()
const openTokenExpire = ref<Boolean>(false)

let oldPath: any = null
const menuVisible = computed(() => {
  return route.query.layout !== 'false'
})

const isUserLoged = computed(() => {
  return userStore.isUserLoged
})

const isEmbed = computed(() => {
  return navigationStore.embed
})

const isPopupWindow = computed(() => {
  return isPopup()
})

const isLogout = computed(() => {
  return navigationStore.logingOut
})

const user = ref(userStore?.user)

const onTokenExpire = (e) => {
  console.log(e.detail, 'TOKEN EXPIRE EVENT')
  openTokenExpire.value = e.detail?.open || false
}

const validateLogin = async () => {
  const isLogout = localStorage.getItem('loggedOutAt')
  if (isLogout) {
    localStorage.removeItem('loggedOutAt')
    await signOut()
    navigateTo('/')
  }
}

onMounted(async () => {
  if (userStore.token) {
    await preload()
  }
  isLoading.value = false
  host.unSubscribeListener(listenerMessageFromMobile)
  host.subscribeListener(listenerMessageFromMobile)
  document.addEventListener('on_token_expire' as string, onTokenExpire)
  window.addEventListener('click', validateLogin)

  if (!navigationStore.embed && !isPopup()) {
    await loadPriorityPatient()
  }
})

watch(
  () => userStore.isUserLoged,
  async (status) => {
    if (!navigationStore.embed && status && !isPopup()) initiateDeviceActivityWorker()
  },
  { immediate: true },
)

watch(
  () => userStore.isTokenExpire,
  async (value) => {
    if (value && route?.path !== '/change-password') navigateTo('/change-password')
  },
  { immediate: true },
)

watch(
  () => userStore.user,
  async (value) => {
    user.value = value
  },
  { immediate: true },
)

const isExternal = computed(() => {
  return navigationStore.external
})

const currentPath = computed(() => {
  const routerName = route?.name as string
  const splitRouterName = routerName?.split('-')
  if (splitRouterName.length > 1) {
    switch (splitRouterName[0]) {
      case 'patients':
        return 'patients'
      case 'map':
        return 'map'
      case 'practitioners':
        return 'practitioners'
      default:
        break
    }
  }

  return route.name as string
})

const listenerMessageFromMobile = (event: any) => {
  try {
    const { data } = event
    switch (data.type) {
      case 'redirect':
        handleAuthToken(data.query.authToken)
        if (data.query && data.id) {
          data.query.id = data.id
        }
        navigateTo({ path: data.path, query: data.query })
        if (oldPath && oldPath === data.path && navigationStore.embed) {
          onRedirectSuccessFromIOS(data.id)
        }
        oldPath = data.path
        break
      case 'redirect-failed':
        reloadUrl.value = `${window.location.origin}${data.payload.path}?${new URLSearchParams(data.payload.query).toString()}`
        isEmbedRedirectError.value = true
        break
      case 'show-overlay':
        isLoading.value = true
        break
      case 'hide-overlay':
        isLoading.value = false
        break
      default:
        break
    }
  } catch (error) {
    console.log(error, event)
  }
}

const onReloadFromEmbed = () => {
  isEmbedRedirectError.value = false
  if (reloadUrl.value !== null) window.location.href = reloadUrl.value as string
}

onUnmounted(() => {
  host.unSubscribeListener(listenerMessageFromMobile)
  window.removeEventListener('click', validateLogin)
  document.removeEventListener('on_token_expire' as string, onTokenExpire)
})
</script>

<style lang="stylus" scoped>
:deep().v-application
    &__wrap
        flex-direction row !important
        overflow hidden

.embed-error
  width 100%
  height 100%
  background #fff
  position absolute
  top 0
  left 0
  z-index 10000
  display flex
  align-items center
  justify-content center
  flex-direction column
  gap 10px
  .v-label
    opacity 1
    font-size 1.2rem
    font-weight 500
    white-space pre-wrap
    text-align center
    padding 30px

.main
  display flex
  height 100%
  width 100%
  .overlay
    position absolute
    z-index 100
    height 100vh
    width 100vw
    top 0
    left 0
    background-color var(--white, #FFFFFF)
    opacity 1
    .loader
      position absolute
      top 50%
      left 50%
  .nav-bar
    width 72px
    position relative
    overflow auto
    background var(--japanese-indigo, #194355)
    display flex
    align-items center
    justify-content flex-start
    z-index 2002
  .content
    background-color var(--white, #FFFFFF)
    flex 1
    transition margin .3s ease-in-out
    width calc(100% - 72px)
</style>
